import React from "react";
import { Link, useLocation } from "react-router-dom";
import { CalendarPlus, Calendar, User, ExternalLink, Compass, Building2, Globe } from "lucide-react";
import { useAuthUser } from "@/hooks/useAuthUser";
import { getCookie } from "@/utils/cookieUtils";

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ElementType;
  current: boolean;
  external?: boolean;
  separator?: boolean;
  visible?: boolean;
}

interface NavigationProps {
  closeMenu?: () => void;
}

export function Navigation({ closeMenu }: NavigationProps) {
  const location = useLocation();
  const { profile, user: clerkUser } = useAuthUser();
  const currentUser = profile; // For compatibility

  // Determine if we should show the CalOne Calendar link
  // Prefer student_email from profile, fallback to username cookie
  const studentEmail = profile?.student_email;
  const username = getCookie("username");
  const showCaloneCalendar = Boolean(studentEmail || username);

  // Use student_email from profile first, then username cookie as fallback
  const calendarIdentifier = studentEmail || username || "";
  const calendarUrl = `https://www.calone.com/${calendarIdentifier}`;

  const navigation = [
    { name: "My Events", href: "/dashboard", icon: Calendar, current: location.pathname === "/dashboard" },
    { name: "Explore Events", href: "/explore", icon: Compass, current: location.pathname === "/explore" },
    { name: "Create Event", href: "/dashboard/new", icon: CalendarPlus, current: location.pathname === "/dashboard/new" },
    { name: "Organization", href: "/organizations", icon: Building2, current: location.pathname.startsWith("/organizations") },
    { name: "Profile", href: "/profile", icon: User, current: location.pathname === "/profile" },
    {
      name: "CalOne Calendar",
      href: calendarUrl,
      icon: Calendar,
      current: location.pathname === calendarUrl,
      external: true,
      visible: showCaloneCalendar,
      separator: showCaloneCalendar,
    },
    {
      name: "Create Meeting",
      href: "https://meet.calone.com/",
      icon: ExternalLink,
      current: false,
      external: true,
      separator: !showCaloneCalendar,
    },
    {
      name: "Trd.One",
      href: "https://www.trd.one",
      icon: Globe,
      current: false,
      external: true,
    },
  ];

  return (
    <nav className="mt-5 flex-1 px-2 space-y-1">
      {navigation
        .filter((item) => item.visible !== false)
        .map((item, index) => (
          <div key={item.name}>
            {item.separator && index > 0 && <div className="my-3 border-t border-border" />}
            <Link
              to={item.href}
              target={item.external ? "_blank" : undefined}
              rel={item.external ? "noopener noreferrer" : undefined}
              onClick={closeMenu}
              className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                item.current
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              }`}
            >
              <item.icon className={`mr-3 h-5 w-5 ${item.current ? "text-primary-foreground" : "text-muted-foreground"}`} />
              {item.name}
            </Link>
          </div>
        ))}
    </nav>
  );
}
